import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/feature/PageHeader';
import { SortTabs } from '@/components/feature/FilterControls';
import { useSeo } from '@/hooks/useSeo';
import { news } from '@/lib/contentService';

const PAGE_SIZE = 5;

export default function NewsPage() {
  useSeo({
    title: '影视资讯 - 玩偶姐姐影视内容平台',
    description:
      '玩偶姐姐影视资讯频道，聚合行业动态、票房市场观察、明星动态与深度影评，每日更新影视圈最新消息。',
    keywords: '影视资讯,影视新闻,行业动态,影评,明星动态,玩偶姐姐资讯',
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    news.forEach((item) => {
      if (item.articleCategory) set.add(item.articleCategory);
    });
    return ['全部', ...Array.from(set)];
  }, []);

  const [category, setCategory] = useState('全部');
  const [sort, setSort] = useState('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const list = news.filter((item) => category === '全部' || item.articleCategory === category);
    return [...list].sort((a, b) => {
      if (sort === 'hot') return (b.heat ?? 0) - (a.heat ?? 0);
      return (b.date ?? '').localeCompare(a.date ?? '');
    });
  }, [category, sort]);

  const featured = useMemo(() => {
    return [...news].sort((a, b) => (b.heat ?? 0) - (a.heat ?? 0))[0];
  }, []);

  const shown = filtered.slice(0, visible);

  return (
    <div className="w-full">
      <PageHeader
        eyebrow="Newsroom"
        title="影视资讯与深度解析"
        description="行业动态、票房数据、明星消息与影评解析。我们每天整理影视圈正在发生的事，并告诉你哪些值得关注。"
        crumbs={[{ label: '影视资讯' }]}
        aside={
          <div className="flex items-center gap-5 rounded-lg border border-background-300 bg-background-50 px-5 py-3">
            <div>
              <p className="font-heading text-xl font-bold leading-none text-foreground-950">{news.length}</p>
              <p className="mt-1 font-label text-[11px] text-foreground-500">累计发布</p>
            </div>
            <span className="h-8 w-px bg-background-300"></span>
            <div>
              <p className="font-heading text-xl font-bold leading-none text-primary-600">每日</p>
              <p className="mt-1 font-label text-[11px] text-foreground-500">持续更新</p>
            </div>
          </div>
        }
      />

      <section className="w-full px-4 py-6 md:px-8 md:py-8">
        {featured && (
          <Link
            to={`/detail/${featured.id}`}
            className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 md:grid-cols-[1.15fr_1fr]"
          >
            <div className="h-[200px] w-full overflow-hidden bg-background-200 md:h-[300px]">
              <img
                src={featured.cover}
                alt={`${featured.title} 配图`}
                title={`${featured.title} 影视资讯`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-center p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-sm bg-primary-500 px-2 py-0.5 font-label text-[10px] font-bold tracking-wide text-background-50">
                  头条
                </span>
                <span className="rounded-sm bg-secondary-100 px-2 py-0.5 font-label text-[10px] font-medium text-secondary-800">
                  {featured.articleCategory}
                </span>
                <span className="font-label text-[11px] text-foreground-400">{featured.date}</span>
              </div>
              <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                {featured.title}
              </h2>
              <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                {featured.summary}
              </p>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-label text-[11px] text-foreground-500">文 / {featured.author}</span>
                <span className="flex items-center gap-1 font-label text-[11px] font-semibold text-primary-600">
                  阅读全文
                  <i className="ri-arrow-right-line text-[12px] transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </div>
          </Link>
        )}

        <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setCategory(item);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    category === item
                      ? 'bg-secondary-500 font-medium text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {item === '全部' ? '全部资讯' : item}
                </button>
              ))}
            </div>
            <SortTabs
              value={sort}
              onChange={setSort}
              options={[
                { label: '最新发布', value: 'latest' },
                { label: '阅读最多', value: 'hot' },
              ]}
            />
          </div>
        </div>

        <div className="mt-5 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {shown.map((item, index) => (
            <article
              key={item.id}
              className={`group grid grid-cols-1 gap-4 p-4 transition-colors hover:bg-background-100 sm:grid-cols-[180px_1fr] md:p-5 ${
                index !== shown.length - 1 ? 'border-b border-background-200' : ''
              }`}
            >
              <Link
                to={`/detail/${item.id}`}
                className="block h-[140px] w-full cursor-pointer overflow-hidden rounded-md bg-background-200 sm:h-[120px]"
              >
                <img
                  src={item.cover}
                  alt={`${item.title} 配图`}
                  title={`${item.title} 资讯配图`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </Link>
              <div className="flex min-w-0 flex-col justify-center">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 font-label text-[10px] font-medium text-accent-900">
                    {item.articleCategory}
                  </span>
                  <span className="font-label text-[11px] text-foreground-400">{item.date}</span>
                  <span className="flex items-center gap-1 font-label text-[11px] text-foreground-400">
                    <i className="ri-eye-line text-[12px]"></i>
                    {(item.heat ?? 60) * 132}
                  </span>
                </div>
                <h3 className="mt-2 line-clamp-2 font-heading text-sm font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                  <Link to={`/detail/${item.id}`} className="cursor-pointer">
                    {item.title}
                  </Link>
                </h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
                <div className="mt-2.5 flex items-center justify-between">
                  <span className="font-label text-[11px] text-foreground-500">文 / {item.author}</span>
                  <Link
                    to={`/detail/${item.id}`}
                    className="flex cursor-pointer items-center gap-1 font-label text-[11px] text-primary-600"
                  >
                    查看
                    <i className="ri-arrow-right-line text-[12px]"></i>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="mt-5 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-newspaper-line text-[26px]"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">该栏目暂无资讯</h3>
            <button
              type="button"
              onClick={() => setCategory('全部')}
              className="mt-5 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50"
            >
              查看全部资讯
            </button>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="mt-7 flex flex-col items-center gap-3">
            {shown.length < filtered.length ? (
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-down-line text-[15px]"></i>
                加载更多资讯
              </button>
            ) : (
              <p className="flex items-center gap-2 font-label text-xs text-foreground-400">
                <i className="ri-check-line text-[13px]"></i>
                已显示全部 {filtered.length} 条资讯
              </p>
            )}
            <p className="font-label text-[11px] text-foreground-400">
              已显示 {shown.length} / {filtered.length}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}